import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'react-toastify';

type Role = 'admin' | 'captain' | null;

interface User {
  id: string;
  email: string;
  team?: string;  // Add team property if this is required for user
  club_id?: string;  // Add club_id so it can be accessed directly from user object
  role?: 'admin' | 'captain'; // Add role for fixture logic
  project_id?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  userRole: Role;
  userClubId: string | null;
  clubName: string | null;
  clubLogoPath: string | null;
  userName: string | null;
  userTeamId: string | null; // Added team_id to context
  userProjectId: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<Role>(null);
  const [userClubId, setUserClubId] = useState<string | null>(null);
  const [userTeamId, setUserTeamId] = useState<string | null>(null); // ✅
  const [userProjectId, setUserProjectId] = useState<string | null>(null);
  const [clubName, setClubName] = useState<string | null>(null);
  const [clubLogoPath, setClubLogoPath] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  const resetUserState = () => {
    console.log('🧼 Resetting user state');
    setUser(null);
    setUserName(null);
    setUserRole(null);
    setUserClubId(null);
    setUserTeamId(null); // ✅
    setUserProjectId(null);
    setClubName(null);
    setClubLogoPath(null);
  };

  const fetchProfile = async (userId: string) => {
    console.log('📥 Fetching profile for user ID:', userId);

    const { data: profile, error } = await supabase
      .from('users')
      .select(`
        name,
        role,
        club_id,
        team_id,
        project_id,
        clubs!club_id (
          name,
          logo_path
        )
      `)
      .eq('id', userId)
      .maybeSingle();

    if (error || !profile) {
      console.error('❌ Failed to fetch profile:', error?.message);
      toast.error("Failed to load profile. Please try again later.");
      resetUserState();
    } else {
      console.log('✅ Profile fetched:', profile);
      setUserName(profile.name ?? null);
      setUserRole(profile.role ?? null);
      setUserClubId(profile.club_id ?? null);
      setUserTeamId(profile.team_id ?? null); // ✅
      setUserProjectId(profile.project_id ?? null);
      // Ensure user object has club_id, role, and project_id at top-level
      setUser((prevUser) => ({
        ...prevUser!,
        club_id: profile.club_id ?? null,
        role: profile.role ?? null,
        project_id: profile.project_id ?? null,
      }));
      const club = Array.isArray(profile.clubs) ? profile.clubs[0] : profile.clubs;
      setClubName(club?.name ?? null);
      setClubLogoPath(club?.logo_path ?? null);
    }
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error || !session?.user) {
          resetUserState();
        } else {
          const currentUser = session.user as User; // Casting supabase User to custom User type
          setUser(currentUser);
          await fetchProfile(currentUser.id);
        }
      } finally {
        setLoading(false);
      }
    };
    getUser();

    // Listen for Supabase auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const currentUser = session.user as User;
        setUser(currentUser);
        fetchProfile(currentUser.id);
      } else {
        resetUserState();
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    resetUserState();
  };

  const value: AuthContextType = {
    user,
    loading,
    logout,
    isAuthenticated: !!user,
    isAdmin: userRole === 'admin',
    userRole,
    userClubId,
    clubName,
    clubLogoPath,
    userName,
    userTeamId, // ✅
    userProjectId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};